import React from 'react';
import { Badge, Tag, Tooltip, Typography } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { colors } from '../../styles/theme';

const { Text } = Typography;

export type StatusType = 'success' | 'error' | 'warning' | 'info' | 'processing' | 'default';

interface StatusConfig {
  color: string;
  icon?: React.ReactNode;
  text: string;
  bgColor?: string;
  borderColor?: string;
}

interface StatusIndicatorProps {
  /** 상태 타입 */
  status: StatusType;
  /** 커스텀 텍스트 */
  text?: string;
  /** 표시 형태 */
  variant?: 'badge' | 'tag' | 'text' | 'dot';
  /** 아이콘 표시 여부 */
  showIcon?: boolean;
  /** 툴팁 메시지 */
  tooltip?: string;
  /** 크기 */
  size?: 'small' | 'default' | 'large';
  /** 커스텀 스타일 */
  style?: React.CSSProperties;
}

/**
 * 통합 상태 표시 컴포넌트
 * 
 * 다양한 상태를 일관된 스타일로 표시하는 컴포넌트입니다.
 * Badge, Tag, Text 등 다양한 형태로 렌더링할 수 있습니다.
 */
const StatusIndicator: React.FC<StatusIndicatorProps> = React.memo(({
  status,
  text,
  variant = 'tag',
  showIcon = true,
  tooltip,
  size = 'default',
  style,
}) => {
  // 상태별 설정
  const statusConfigs: Record<StatusType, StatusConfig> = {
    success: {
      color: colors.success[500],
      icon: <CheckCircleOutlined />,
      text: text || '성공',
      bgColor: colors.success[50],
      borderColor: colors.success[200],
    },
    error: {
      color: colors.error[500],
      icon: <CloseCircleOutlined />,
      text: text || '오류',
      bgColor: colors.error[50],
      borderColor: colors.error[200],
    },
    warning: {
      color: colors.warning[500],
      icon: <ExclamationCircleOutlined />,
      text: text || '경고',
      bgColor: colors.warning[50],
      borderColor: colors.warning[200],
    },
    info: {
      color: colors.info[500],
      icon: <ExclamationCircleOutlined />,
      text: text || '정보',
      bgColor: colors.info[50],
      borderColor: colors.info[200],
    },
    processing: {
      color: colors.primary[500],
      icon: <SyncOutlined spin />,
      text: text || '처리중',
      bgColor: colors.primary[50],
      borderColor: colors.primary[200],
    },
    default: {
      color: colors.gray[500],
      icon: undefined,
      text: text || '기본',
      bgColor: colors.gray[50],
      borderColor: colors.gray[200],
    },
  };

  const config = statusConfigs[status];

  const getSizeConfig = () => {
    switch (size) {
      case 'small':
        return { fontSize: 12, padding: '2px 6px', iconSize: 12 };
      case 'large':
        return { fontSize: 16, padding: '6px 12px', iconSize: 16 };
      default:
        return { fontSize: 14, padding: '4px 8px', iconSize: 14 };
    }
  };

  const sizeConfig = getSizeConfig();

  const renderContent = () => {
    const iconElement = showIcon && config.icon ? (
      <span style={{ 
        marginRight: config.text ? 4 : 0,
        fontSize: sizeConfig.iconSize,
      }}>
        {config.icon}
      </span>
    ) : null;

    const textElement = config.text ? (
      <span>{config.text}</span>
    ) : null;

    return (
      <>
        {iconElement}
        {textElement}
      </>
    );
  };

  const renderIndicator = () => {
    const content = renderContent();
    
    switch (variant) {
      case 'badge':
        return (
          <Badge
            status={status === 'processing' ? 'processing' : 'default'}
            color={config.color}
            text={config.text}
            style={style}
          />
        );

      case 'tag':
        return (
          <Tag
            color={config.color}
            style={{
              backgroundColor: config.bgColor,
              borderColor: config.borderColor,
              color: config.color,
              fontSize: sizeConfig.fontSize,
              padding: sizeConfig.padding,
              borderRadius: 4,
              display: 'inline-flex',
              alignItems: 'center',
              ...style,
            }}
          >
            {content}
          </Tag>
        );

      case 'text':
        return (
          <Text
            style={{
              color: config.color,
              fontSize: sizeConfig.fontSize,
              display: 'inline-flex',
              alignItems: 'center',
              ...style,
            }}
          >
            {content}
          </Text>
        );

      case 'dot':
        return (
          <div
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontSize: sizeConfig.fontSize,
              ...style,
            }}
          >
            <div
              style={{
                width: sizeConfig.iconSize,
                height: sizeConfig.iconSize,
                borderRadius: '50%',
                backgroundColor: config.color,
                marginRight: config.text ? 6 : 0,
              }}
            />
            {config.text && <span>{config.text}</span>}
          </div>
        );

      default:
        return content;
    }
  };

  const indicator = renderIndicator();

  if (tooltip) {
    return <Tooltip title={tooltip}>{indicator}</Tooltip>;
  }

  return indicator;
});

StatusIndicator.displayName = 'StatusIndicator';

export default StatusIndicator;